/**
 * DOCUMENT CONFIG RESOLVER
 *
 * Merges static YAML configuration (from document-configs.yaml) with
 * runtime-only code (icon components, condition functions, gate functions)
 * to produce fully-resolved DocumentConfig objects compatible with the
 * existing DocumentConfig interface used by DocumentModal.
 */

import type { DocumentConfigYaml, ConversionYaml, FinalActionMenuItemYaml } from './types';
import { resolveLucideIcon } from '@/lib/finance/iconResolver';
import {
  FINAL_ACTION_GATES,
  FINAL_ACTION_ITEM_CONDITIONS,
} from './finalActionGates';
import type {
  DocumentConfig,
  FinalActionMenuItem,
  FinalActionMenuConfig,
} from '@/components/finance/modals/documentModalHelpers';
import type { Document, DocumentType } from '@/hooks/useDocumentsWorkItems';

/**
 * Resolve a YAML conversion entry into a DocumentConfig conversion
 * with a real icon component and a condition function.
 */
function resolveConversion(
  conv: ConversionYaml,
): DocumentConfig['conversions'] extends Array<infer T> | undefined ? T : never {
  const icon = resolveLucideIcon(conv.icon);

  let condition: ((doc: Document) => boolean) | undefined;

  if (conv.always_true) {
    condition = () => true;
  } else if (conv.condition_statuses) {
    condition = (doc: Document) => conv.condition_statuses!.includes(doc.status!);
  } else if (conv.condition_exclude_statuses) {
    condition = (doc: Document) => !conv.condition_exclude_statuses!.includes(doc.status!);
  }

  return {
    targetType: conv.target_type as DocumentType,
    label: conv.label,
    icon,
    condition,
  };
}

/**
 * Resolve a YAML final action menu item into a FinalActionMenuItem
 * with real icon component and condition function.
 */
function resolveFinalActionMenuItem(
  item: FinalActionMenuItemYaml,
): FinalActionMenuItem {
  const resolved: FinalActionMenuItem = {
    type: item.type as FinalActionMenuItem['type'],
    label: item.label,
    tone: item.tone as FinalActionMenuItem['tone'],
  };

  if (item.icon) {
    resolved.icon = resolveLucideIcon(item.icon);
  }

  if (item.condition_name && FINAL_ACTION_ITEM_CONDITIONS[item.condition_name]) {
    resolved.condition = FINAL_ACTION_ITEM_CONDITIONS[item.condition_name];
  }

  return resolved;
}

/**
 * Resolve a YAML final action menu into a FinalActionMenuConfig
 * with the gate function and resolved items.
 */
function resolveFinalActionMenu(
  yaml: DocumentConfigYaml,
): FinalActionMenuConfig | undefined {
  if (!yaml.final_action_menu) return undefined;

  const gateFn = yaml.final_action_menu.gate_type
    ? FINAL_ACTION_GATES[yaml.final_action_menu.gate_type]
    : undefined;

  return {
    gate: gateFn,
    items: yaml.final_action_menu.items.map(resolveFinalActionMenuItem),
  };
}

/**
 * Resolve a full DocumentConfigYaml into a DocumentConfig
 * compatible with the existing interface.
 */
export function resolveDocumentConfig(
  docType: string,
  yaml: DocumentConfigYaml,
): DocumentConfig {
  return {
    doc_type: docType as DocumentType,

    labels: {
      title: yaml.labels.title,
      titleNew: yaml.labels.titleNew,
      titleEdit: yaml.labels.titleEdit,
      numberField: yaml.labels.numberField,
      numberPlaceholder: yaml.labels.numberPlaceholder,
      dateField: yaml.labels.dateField,
      contactLabel: yaml.labels.contactLabel,
      saveButton: yaml.labels.saveButton,
    },

    fields: {
      ...yaml.fields,
      contactTypes: yaml.fields.contact_types,
      statusOptions: yaml.status_options,
      userEditableStatuses: yaml.user_editable_statuses,
      invoiceTypeOptions: yaml.invoice_type_options,
      fulfillmentStatusOptions: yaml.fulfillment_status_options,
      qualityCheckStatusOptions: yaml.quality_check_status_options,
      approvalStatusOptions: yaml.approval_status_options,
    },

    conversions: (yaml.conversions || []).map(resolveConversion),

    finalActionMenu: resolveFinalActionMenu(yaml),
  };
}
